import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { NotesService } from '../../services/notes.service'
import { AircraftNote } from '../../types/database'
import { useAuth } from '../../contexts/AuthContext'
import { useProfile } from '../../hooks/useProfile'
import EditTimeCountdown from '../market/EditTimeCountdown'

type NoteWithAuthor = AircraftNote & { author_name: string }

interface QuickNotePopoverProps {
  aircraftId: number
  serialNumber: string | null
  noteCount: number
  onNoteSaved: () => void
  onClose: () => void
  anchorEl: HTMLElement | null
}

/**
 * Convert a Date to a local datetime-local input value (YYYY-MM-DDTHH:MM)
 */
function toLocalDatetimeString(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export default function QuickNotePopover({ aircraftId, serialNumber, noteCount: _noteCount, onNoteSaved, onClose, anchorEl }: QuickNotePopoverProps) {
  const { user } = useAuth()
  const { profile } = useProfile()
  const [content, setContent] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [, setSaved] = useState(false)
  const popoverRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Timestamp state for new notes
  const [newTimestamp, setNewTimestamp] = useState(() => toLocalDatetimeString(new Date()))
  const [useCustomNewTimestamp, setUseCustomNewTimestamp] = useState(false)

  // Existing notes state
  const [notes, setNotes] = useState<NoteWithAuthor[]>([])
  const [notesLoading, setNotesLoading] = useState(true)

  // Edit/delete state
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [editTimestamp, setEditTimestamp] = useState('')
  const [useCustomEditTimestamp, setUseCustomEditTimestamp] = useState(false)
  const [editSaving, setEditSaving] = useState(false)
  const [deleting, setDeleting] = useState<string | null>(null)

  const isAdmin = profile?.account_type === 'admin' || profile?.account_type === 'global_admin'

  const canEditNote = (note: NoteWithAuthor): boolean => {
    if (!user) return false
    return NotesService.canEditNote(note, user.id, isAdmin)
  }

  // Fetch existing notes on mount
  useEffect(() => {
    fetchNotes()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraftId])

  const fetchNotes = async () => {
    try {
      setNotesLoading(true)
      const fetched = await NotesService.getByAircraftIdWithAuthors(aircraftId)
      setNotes(fetched)
    } catch {
      // Silently fail — the note input still works
    } finally {
      setNotesLoading(false)
    }
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  // Focus textarea on mount
  useEffect(() => {
    setTimeout(() => textareaRef.current?.focus(), 50)
  }, [])

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
          anchorEl && !anchorEl.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose, anchorEl])

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (editingNoteId) {
          setEditingNoteId(null)
          setEditContent('')
        } else {
          onClose()
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose, editingNoteId])

  const handleSave = async () => {
    if (!content.trim()) {
      setError('Note cannot be empty')
      return
    }

    try {
      setSaving(true)
      setError(null)

      const customTimestamp = useCustomNewTimestamp && newTimestamp
        ? new Date(newTimestamp).toISOString()
        : undefined

      await NotesService.create({
        aircraft_id: aircraftId,
        content: content.trim(),
        created_at: customTimestamp
      })
      setSaved(true)
      onNoteSaved()
      // Clear the input and reset timestamp
      setContent('')
      setUseCustomNewTimestamp(false)
      setNewTimestamp(toLocalDatetimeString(new Date()))
      setSaved(false)
      await fetchNotes()
    } catch (err: any) {
      setError(err.message || 'Failed to save note')
    } finally {
      setSaving(false)
    }
  }

  const handleEditSave = async (noteId: string) => {
    if (!editContent.trim()) return

    try {
      setEditSaving(true)

      const customTimestamp = useCustomEditTimestamp && editTimestamp
        ? new Date(editTimestamp).toISOString()
        : undefined

      await NotesService.update(noteId, {
        content: editContent.trim(),
        created_at: customTimestamp
      })
      setEditingNoteId(null)
      setEditContent('')
      setUseCustomEditTimestamp(false)
      setEditTimestamp('')
      await fetchNotes()
    } catch (err: any) {
      alert('Failed to update note: ' + err.message)
    } finally {
      setEditSaving(false)
    }
  }

  const handleDelete = async (noteId: string) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return

    try {
      setDeleting(noteId)
      await NotesService.delete(noteId)
      setNotes(prev => prev.filter(n => n.id !== noteId))
      onNoteSaved()
    } catch (err: any) {
      alert('Failed to delete note: ' + err.message)
    } finally {
      setDeleting(null)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Cmd/Ctrl+Enter to save
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      handleSave()
    }
  }

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9998] bg-gray-600 bg-opacity-50 flex items-center justify-center animate-fadeIn"
      onClick={onClose}
    >
      <div
        ref={popoverRef}
        className="z-[9999] w-[72rem] max-w-[90vw] max-h-[85vh] flex flex-col bg-white rounded-lg shadow-xl border border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-purple-50 rounded-t-lg shrink-0">
          <span className="text-sm font-semibold text-purple-900">
            Notes for SN {serialNumber || 'Unknown'}
          </span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* New note input */}
        <div className="p-5 border-b border-gray-200 shrink-0">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={4}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 resize-none"
            placeholder="Type your note..."
            disabled={saving}
          />

          {/* Timestamp for new note */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-medium text-gray-700">Timestamp</label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomNewTimestamp}
                  onChange={(e) => {
                    setUseCustomNewTimestamp(e.target.checked)
                    if (!e.target.checked) {
                      setNewTimestamp(toLocalDatetimeString(new Date()))
                    }
                  }}
                  className="sr-only peer"
                  disabled={saving}
                />
                <div className="relative w-8 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-purple-600"></div>
                <span className="ms-2 text-xs text-gray-500">Custom</span>
              </label>
            </div>
            {useCustomNewTimestamp ? (
              <input
                type="datetime-local"
                value={newTimestamp}
                onChange={(e) => setNewTimestamp(e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                disabled={saving}
              />
            ) : (
              <div className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-600">
                Now (current time)
              </div>
            )}
          </div>

          {error && (
            <div className="mt-2 text-sm text-red-600">{error}</div>
          )}

          <div className="flex items-center justify-between mt-3">
            <span className="text-sm text-gray-400">⌘+Enter to save</span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={saving}
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !content.trim()}
                className="px-4 py-2 text-sm text-white rounded-md transition-colors disabled:opacity-50 bg-purple-700 hover:bg-purple-800"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>

        {/* Existing notes list */}
        <div className="flex-1 overflow-y-auto p-5 min-h-0">
          {notesLoading ? (
            <div className="text-center py-6">
              <div className="text-sm text-gray-400">Loading notes...</div>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-6">
              <div className="text-sm text-gray-400">No notes yet for this aircraft</div>
            </div>
          ) : (
            <div className="space-y-3">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-150"
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="text-xs text-gray-500">
                      {formatDate(note.created_at)}{' '}
                      <span className="font-semibold text-purple-600">{note.author_name}</span>
                    </div>
                    {canEditNote(note) && editingNoteId !== note.id && (
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => {
                            setEditingNoteId(note.id)
                            setEditContent(note.content)
                            setEditTimestamp(toLocalDatetimeString(new Date(note.created_at)))
                            setUseCustomEditTimestamp(false)
                          }}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          disabled={deleting === note.id}
                          className="text-xs text-red-400 hover:text-red-600 disabled:opacity-50"
                        >
                          {deleting === note.id ? 'Deleting...' : 'Delete'}
                        </button>
                        {!isAdmin && (
                          <EditTimeCountdown
                            createdAt={note.created_at}
                            className="ml-1"
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {editingNoteId === note.id ? (
                    <div>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            e.preventDefault()
                            handleEditSave(note.id)
                          }
                        }}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 resize-none"
                        disabled={editSaving}
                        autoFocus
                      />
                      {/* Timestamp for editing */}
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <label className="text-xs font-medium text-gray-700">Timestamp</label>
                          <label className="inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={useCustomEditTimestamp}
                              onChange={(e) => {
                                setUseCustomEditTimestamp(e.target.checked)
                                if (!e.target.checked) {
                                  setEditTimestamp(toLocalDatetimeString(new Date(note.created_at)))
                                }
                              }}
                              className="sr-only peer"
                              disabled={editSaving}
                            />
                            <div className="relative w-8 h-4 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-purple-600"></div>
                            <span className="ms-2 text-xs text-gray-500">Custom</span>
                          </label>
                        </div>
                        {useCustomEditTimestamp ? (
                          <input
                            type="datetime-local"
                            value={editTimestamp}
                            onChange={(e) => setEditTimestamp(e.target.value)}
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                            disabled={editSaving}
                          />
                        ) : (
                          <div className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-xs text-gray-600">
                            {new Date(note.created_at).toLocaleString('en-US', {
                              month: 'numeric',
                              day: 'numeric',
                              year: '2-digit',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          onClick={() => {
                            setEditingNoteId(null)
                            setEditContent('')
                            setUseCustomEditTimestamp(false)
                            setEditTimestamp('')
                          }}
                          disabled={editSaving}
                          className="px-3 py-1 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleEditSave(note.id)}
                          disabled={editSaving || !editContent.trim()}
                          className="px-3 py-1 text-xs text-white bg-purple-700 hover:bg-purple-800 rounded-md transition-colors disabled:opacity-50"
                        >
                          {editSaving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap">
                      {note.content}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}

